/**
 * chatPrefs.ts
 *
 * Persistence and geometry helpers for draggable, collapsible, and customizable Chat.
 */

export interface ChatPrefs {
  fontSize: 'compact' | 'normal' | 'large';
  opacity: number; // 0.35, 0.55, 0.8, 0.95
}

export interface ChatPosition {
  x: number;
  y: number;
}

export const CHAT_STORAGE_KEYS = {
  prefs: 'h2k_chat_prefs',
  position: 'h2k_chat_position',
  collapsed: 'h2k_chat_collapsed',
} as const;

export const DEFAULT_CHAT_PREFS: ChatPrefs = {
  fontSize: 'normal',
  opacity: 0.55,
};

export function loadChatPrefs(): ChatPrefs {
  if (typeof window === 'undefined' || !window.localStorage) {
    return DEFAULT_CHAT_PREFS;
  }
  try {
    const raw = window.localStorage.getItem(CHAT_STORAGE_KEYS.prefs);
    if (!raw) return DEFAULT_CHAT_PREFS;
    const parsed = JSON.parse(raw);
    return {
      fontSize: ['compact', 'normal', 'large'].includes(parsed.fontSize) ? parsed.fontSize : 'normal',
      opacity: typeof parsed.opacity === 'number' && parsed.opacity >= 0.2 && parsed.opacity <= 1.0 ? parsed.opacity : 0.55,
    };
  } catch {
    return DEFAULT_CHAT_PREFS;
  }
}

export function saveChatPrefs(prefs: ChatPrefs): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(CHAT_STORAGE_KEYS.prefs, JSON.stringify(prefs));
  } catch {
    /* storage quota / sandbox */
  }
}

export function loadChatPosition(): ChatPosition | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(CHAT_STORAGE_KEYS.position);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
      return { x: parsed.x, y: parsed.y };
    }
  } catch {
    /* fallback to null */
  }
  return null;
}

export function saveChatPosition(pos: ChatPosition | null): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (pos === null) {
      window.localStorage.removeItem(CHAT_STORAGE_KEYS.position);
    } else {
      window.localStorage.setItem(CHAT_STORAGE_KEYS.position, JSON.stringify(pos));
    }
  } catch {
    /* ignore */
  }
}

export function getFontSizePixels(fontSize: ChatPrefs['fontSize']): number {
  switch (fontSize) {
    case 'compact':
      return 11;
    case 'large':
      return 14.5;
    case 'normal':
    default:
      return 12.5;
  }
}

export function clampChatPosition(
  pos: ChatPosition,
  width: number,
  height: number,
  viewportW = typeof window !== 'undefined' ? window.innerWidth : 1280,
  viewportH = typeof window !== 'undefined' ? window.innerHeight : 720,
): ChatPosition {
  const maxX = Math.max(0, viewportW - width);
  const maxY = Math.max(0, viewportH - height);
  return {
    x: Math.max(0, Math.min(maxX, Math.round(pos.x))),
    y: Math.max(0, Math.min(maxY, Math.round(pos.y))),
  };
}
